import React from "react";
import { Link } from "react-router-dom";
import "./Resources.css";
import '@fortawesome/fontawesome-free/css/all.min.css';

const Resources = () => {
  const guides = [
    {
      title: "Understanding Divorce Law in England",
      desc: "A comprehensive guide to the divorce process, from initial petition to final decree.",
      category: "Divorce",
      readTime: "15 min read",
      content: [
        "The no-fault divorce system in England and Wales",
        "How to file for divorce: step-by-step process",
        "Timeline and key stages of divorce proceedings",
        "Required documentation and forms",
        "Costs and fees associated with divorce",
        "What happens after the divorce is finalized"
      ]
    },
    {
      title: "Child Arrangement Orders: What You Need to Know",
      desc: "Learn about custody arrangements, contact schedules, and protecting your children's interests.",
      category: "Children",
      readTime: "12 min read",
      content: [
        "Types of child arrangement orders",
        "Residence and contact arrangements explained",
        "Factors courts consider in child arrangement decisions",
        "How to apply for a child arrangement order",
        "Modifying existing arrangements",
        "Enforcement and what to do if orders are breached"
      ]
    },
    {
      title: "Protecting Your Financial Interests",
      desc: "Essential advice on asset division, pensions, and securing your financial future.",
      category: "Financial",
      readTime: "18 min read",
      content: [
        "Understanding financial settlements in divorce",
        "How assets are divided: the legal framework",
        "Pension sharing and offsetting explained",
        "Spousal maintenance: when and how much",
        "Property division and ownership disputes",
        "Protecting your financial interests during separation"
      ]
    },
    {
      title: "Preparing for Mediation: A Checklist",
      desc: "Tips and strategies to help you approach mediation with confidence and clarity.",
      category: "Mediation",
      readTime: "10 min read",
      content: [
        "What is family mediation and when is it appropriate",
        "How to prepare for your first mediation session",
        "What to bring and documents you'll need",
        "Effective communication strategies during mediation",
        "Understanding mediation outcomes and agreements",
        "When mediation might not be the right choice"
      ]
    },
    {
      title: "Pre-Nuptial Agreements: A Complete Guide",
      desc: "Everything you need to know about protecting your assets before marriage.",
      category: "Agreements",
      readTime: "14 min read",
      content: [
        "What is a pre-nuptial agreement and why you might need one",
        "Legal requirements for valid pre-nuptial agreements",
        "What can and cannot be included",
        "How pre-nuptial agreements are enforced",
        "When to review and update your agreement",
        "International considerations for pre-nuptial agreements"
      ]
    },
    {
      title: "Domestic Abuse: Legal Protection and Support",
      desc: "Understanding your rights and the legal protections available to you.",
      category: "Protection",
      readTime: "12 min read",
      content: [
        "Recognizing domestic abuse: types and signs",
        "Non-molestation orders: what they are and how to get one",
        "Occupation orders: protecting your home",
        "Emergency protection: urgent applications",
        "Support services and resources available",
        "Criminal vs. civil remedies for domestic abuse"
      ]
    }
  ];

  const categories = ["All", "Divorce", "Children", "Financial", "Mediation", "Agreements", "Protection"];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1>Legal Guides & Resources</h1>
          <p className="page-hero-subtitle">
            Helpful articles and guides written by Sandy to help you understand your rights and navigate family law matters
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="page-content">
        <div className="page-wrapper">
          {/* Intro */}
          <div className="resources-intro">
            <h2>Expert Legal Guidance at Your Fingertips</h2>
            <p>
              Understanding family law can be complex. Our comprehensive guides break down important legal concepts, 
              processes, and your rights in clear, accessible language. Whether you're just starting to explore your options 
              or need detailed information about a specific area, these resources are designed to help.
            </p>
          </div>

          {/* Guides Grid */}
          <div className="resources-grid">
            {guides.map((guide, index) => (
              <div className="resource-card" key={index}>
                <div className="resource-header">
                  <span className="resource-category">{guide.category}</span>
                  <span className="resource-time">{guide.readTime}</span>
                </div>
                <h3>{guide.title}</h3>
                <p className="resource-desc">{guide.desc}</p>
                <div className="resource-content">
                  <h4>Topics Covered:</h4>
                  <ul>
                    {guide.content.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="resource-footer">
                  <button className="resource-btn">Read Full Guide <i className="fa-solid fa-arrow-right"></i></button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Resources */}
          <div className="additional-resources">
            <h2>Additional Support Resources</h2>
            <div className="support-grid">
              <div className="support-card">
                <div className="support-icon"><i className="fa-solid fa-phone"></i></div>
                <h4>Free Consultation</h4>
                <p>Book a free initial consultation to discuss your specific situation and get personalized advice.</p>
                <Link to="/contact" className="support-link">Book Now <i className="fa-solid fa-arrow-right"></i></Link>
              </div>
              <div className="support-card">
                <div className="support-icon"><i className="fa-solid fa-file-pen"></i></div>
                <h4>Legal Questionnaire</h4>
                <p>Complete our online questionnaire to help us understand your case before your consultation.</p>
                <Link to="/legal-questionnaire" className="support-link">Get Started <i className="fa-solid fa-arrow-right"></i></Link>
              </div>
              <div className="support-card">
                <div className="support-icon"><i className="fa-solid fa-circle-question"></i></div>
                <h4>FAQs</h4>
                <p>Find answers to commonly asked questions about family law, divorce, and our services.</p>
                <a href="#faq" className="support-link">View FAQs <i className="fa-solid fa-arrow-right"></i></a>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="page-cta">
            <h2>Need Personalized Legal Advice?</h2>
            <p>While these guides provide general information, every case is unique. Book a consultation for tailored advice.</p>
            <Link to="/contact" className="cta-button">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
